import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import enUS from '../../../locales/en-US.json';
import esMX from '../../../locales/es-MX.json';
import koKR from '../../../locales/ko-KR.json';
import zhCN from '../../../locales/zh-CN.json';

/**
 * react-i18next setup. M3.9 ships four locales — English, Spanish, Korean,
 * Chinese — for the self-registration flow; every later screen reuses the
 * same `useTranslation()` plumbing so adding a language is a translation
 * job, not a code job.
 *
 * The locale precedence at runtime (Phase 2 §14.2) is:
 *   1. The current user's PreferredCulture (returned by /api/v1/auth/me — TBD)
 *   2. localStorage `invetrak.locale` (manual user pick that persists)
 *   3. Browser `navigator.language` (LanguageDetector)
 *   4. Platform default: `en-US`
 */

export const SUPPORTED_LOCALES = [
  { code: 'en-US', shortLabel: 'EN', nativeLabel: 'English' },
  { code: 'es-MX', shortLabel: 'ES', nativeLabel: 'Español' },
  { code: 'ko-KR', shortLabel: '한국어', nativeLabel: '한국어' },
  { code: 'zh-CN', shortLabel: '中文', nativeLabel: '中文' },
] as const;

export type SupportedLocaleCode = (typeof SUPPORTED_LOCALES)[number]['code'];

export const DEFAULT_LOCALE: SupportedLocaleCode = 'en-US';

await i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      'en-US': { translation: enUS },
      'es-MX': { translation: esMX },
      'ko-KR': { translation: koKR },
      'zh-CN': { translation: zhCN },
    },
    // Resources are region-coded (en-US, es-MX, ko-KR, zh-CN). Map bare
    // browser codes (`en`, `es`, `ko`, `zh`) to their region variant so a
    // browser reporting `es` still gets Spanish; everything else lands on
    // en-US. This replaces `nonExplicitSupportedLngs: true`, which is BROKEN
    // with region-coded supportedLngs — it strips the region from the code
    // being checked (`en-US` → `en`), then compares against the full region
    // codes in supportedLngs, so EVERY language is rejected and only raw
    // translation keys render (observed on portal.invetrak.com, 2026-07-29).
    fallbackLng: {
      en: ['en-US'],
      es: ['es-MX'],
      ko: ['ko-KR'],
      zh: ['zh-CN'],
      default: [DEFAULT_LOCALE],
    },
    supportedLngs: SUPPORTED_LOCALES.map((l) => l.code),
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'invetrak.locale',
    },
  });

export default i18n;
